/**
 * Décide si la hauteur de l'application doit être forcée, et à quelle valeur.
 *
 * Fonction pure, séparée du hook pour être testable : c'est une décision à
 * trois cas dont deux sont des pièges, et elle mérite mieux qu'un essai sur
 * téléphone.
 *
 * # Ce qu'on corrige vraiment
 *
 * Le besoin d'origine est **iOS** : quand le clavier s'ouvre, Safari ne
 * rétrécit pas le layout viewport. `100dvh` continue de désigner l'écran
 * entier, clavier compris, et le champ de saisie se retrouve derrière lui.
 * Recopier `visualViewport.height` règle le problème.
 *
 * # Pourquoi on ne le fait PAS tout le temps
 *
 * Sur Android, `visualViewport` bouge en permanence pendant le défilement,
 * parce que la barre d'adresse se replie et se déplie. Écrire `--app-height`
 * et `--app-offset` à chaque événement redimensionne et déplace, **en pleine
 * inertie de défilement**, le conteneur `position: fixed` qui porte la liste.
 * Le compositeur fait alors défiler des tuiles que le thread principal
 * invalide sous lui : il en résulte des **bandes non peintes** — les zones
 * noires — à des endroits arbitraires de la liste. Android rétrécissant déjà
 * son layout viewport à l'ouverture du clavier, cette écriture ne lui apportait
 * rien : elle ne faisait que casser son rendu.
 *
 * La correction ne s'applique donc que lorsque le clavier masque réellement
 * une partie de la fenêtre — c'est-à-dire sur iOS, et seulement clavier ouvert.
 */

/**
 * En deçà de ce nombre de pixels masqués, l'écart s'explique par la barre
 * d'adresse ou un arrondi, pas par un clavier. Un clavier logiciel occupe au
 * bas mot le quart d'un écran de téléphone.
 */
export const KEYBOARD_THRESHOLD = 120;

export interface ViewportOverride {
  height: number;
  offset: number;
}

/**
 * `null` signifie « ne rien forcer » : le CSS retombe sur `100dvh`, que le
 * navigateur sait tenir à jour tout seul et sans repeindre la liste.
 */
export function viewportOverride(
  innerHeight: number,
  visualHeight: number,
  offsetTop: number,
): ViewportOverride | null {
  const hidden = innerHeight - visualHeight;
  if (!(hidden > KEYBOARD_THRESHOLD)) return null;

  // Arrondi à l'entier : le sous-pixel n'apporte rien et provoquerait une
  // réécriture du style à chaque frame.
  return { height: Math.round(visualHeight), offset: Math.round(offsetTop) };
}
